import { IHttpService, IPromise, IQService, IRootScopeService } from "angular";

import {
  ILoadListQueryParams,
  IListPropertyTypes,
  IList,
  IListSort,
  IListColumn,
  AccessTypes,
  DynamicFilters,
  SupportedOperators,
  ListsPropertyNames,
  ListFilterTypes,
  ListTypes,
  IFilterByFeatureFlag,
  OrListFiltersGroup,
  ICreateDefaultList,
  IPatchList,
} from "../models/models";
import { ICollection } from "@gtmhub/models";
import { EnvironmentService } from "@gtmhub/env";
import { localize } from "@gtmhub/localization";
import { capitalize } from "@gtmhub/util";
import { IRestLayerRequest } from "@gtmhub/core";
import { getCurrentUserId } from "@gtmhub/users";
import { FeatureToggleService } from "@gtmhub/featureToggle/feature-toggle-service";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { ListEvents } from "./events";

export class ListService {
  public static $inject = ["$http", "EnvironmentService", "FeatureToggleService", "$rootScope", "$q"];
  private events: ListEvents;

  constructor(
    private $http: IHttpService,
    private env: EnvironmentService,
    private featureToggleService: FeatureToggleService,
    private $rootScope: IRootScopeService,
    private $q: IQService
  ) {
    this.events = new ListEvents(this.$rootScope);
  }

  // The type is ICollection of any because the items can be goals, metrics, tasks, etc...
  public loadList(params: ILoadListQueryParams): IPromise<ICollection<unknown>> {
    const url: string = this.env.getApiEndpoint(`/lists/${params.id}/load`);

    return this.$http.post<ICollection<unknown>>(url, params.body).then((response) => response.data);
  }

  public getListPropertiesByType(targetType: string): IPromise<IListPropertyTypes> {
    const url: string = this.env.getApiEndpoint(`/lists/${targetType}/schema`);

    return this.$http.get<IListPropertyTypes>(url).then((response) => {
      const dictionary = this.getDictionaryByListType(targetType);
      return this.filterSchemaByFeature(response.data).then(() => {
        const properties = response.data;
        for (const field of properties.fields) {
          if (field.fieldName in dictionary) {
            field.trackingName = field.displayName;
            field.displayName = dictionary[field.fieldName];
          }

          if (field.isFilterable && field.dataType === "numeric") {
            field.supportedOperators = field.supportedOperators.filter((operator) => operator !== "isBetween" && operator !== "isNotBetween");
          }
        }
        return properties;
      });
    });
  }

  public getLists(params: IRestLayerRequest): IPromise<ICollection<IList>> {
    const url = this.env.getApiEndpoint("/lists");
    return this.$http.get<ICollection<IList>>(url, { params }).then((response) => response.data);
  }

  public getList(id: string, params?: IRestLayerRequest): IPromise<IList> {
    const url = this.env.getApiEndpoint(`lists/${id}`);
    const payload: { params: IRestLayerRequest } = params ? { params } : null;
    return this.$http.get<IList>(url, payload).then((response) => {
      return this.filterListColumnSortAndRuleBoundsByFeature(response.data).then(() => {
        return response.data;
      });
    });
  }

  public updateList(list: { id: string; params: IPatchList }): IPromise<IList> {
    const url = this.env.getApiEndpoint(`lists/${list.id}`);

    return this.$http.patch<IList>(url, list.params).then((response) => {
      this.events.broadcastListUpdated(list.id, list.params);
      return response.data;
    });
  }

  public deleteList(list: { id: string }): IPromise<void> {
    const url = this.env.getApiEndpoint(`lists/${list.id}`);

    return this.$http.delete<void>(url).then((response) => {
      this.events.broadcastListDeleted(list.id);
      return response.data;
    });
  }

  public createList(list: ICreateDefaultList): IPromise<IList> {
    const url = this.env.getApiEndpoint(`lists`);

    return this.$http.post<IList>(url, list).then((response) => response.data);
  }

  public cloneList(list: { id: string }): IPromise<IList> {
    const url = this.env.getApiEndpoint(`lists/${list.id}/clone`);

    return this.$http
      .post<IList>(url, list, {
        params: { gtmhubAdditionalParams: { cloned: true } },
      })
      .then((response) => response.data);
  }

  public goalPropertiesDictionary(): Record<string, string> {
    return {
      ownerId: localize("owner_cap"),
      ownerIds: localize("objective_owner"),
      childrenCount: localize("child_objectives"),
      attainment: localize("progress_cap"),
      sessionId: localize("session_cap"),
      sessionTitle: localize("session_cap"),
      dateCreated: localize("creation_date_cap"),
      dateFrom: localize("starting_date_cap"),
      dateTo: localize("ending_date_cap"),
      createdById: localize("the_user_who_created_cap"),
      metricsCount: localize("number_of_key_results_cap"),
      parentId: localize("parent_cap"),
      parentGoalSessionId: localize("parent_session_cap"),
      tasksCount: localize("number_of_tasks_cap"),
      designScore: localize("design_score_cap"),
      tags: localize("tags_cap"),
      name: localize("objective_name"),
      description: localize("description_cap"),
      closedStatus: localize("okr_state"),
      "workflow.status": localize("approval_status"),
      "designScore.totalPoints": localize("okr_design_score"),
    };
  }

  public getListPropertiesDictionary(): Record<string, string> {
    return {
      title: localize("name_cap"),
      uiAccess: localize("access_cap"),
      navigationShow: localize("show_in_navigation_cap"),
      ownerId: localize("owner_cap"),
    };
  }

  public getListAccessType(list: IList): string {
    for (const p of list.access.permissions) {
      if (p.principalKind === "account" && p.grant.general.indexOf("read") !== -1 && p.grant.general.indexOf("update") === -1) {
        return AccessTypes.publicRead;
      }

      if (p.principalKind === "account" && p.grant.general.indexOf("read") !== -1 && p.grant.general.indexOf("update") !== -1) {
        return AccessTypes.publicUpdate;
      }
    }

    if (list.access.permissions.length === 0) {
      return AccessTypes.private;
    }

    return AccessTypes.restricted;
  }

  private predefinedListRulesByType(currentUserId: string) {
    return {
      [ListTypes.Objective]: {
        predefinedColumns: [
          { fieldName: ListsPropertyNames.name, type: ListFilterTypes.alphabetic, width: 250 },
          ...[{ fieldName: ListsPropertyNames.closedStatus, type: ListFilterTypes.alphabetic, width: 250 }],
          { fieldName: ListsPropertyNames.ownerIds, type: ListFilterTypes.id, width: 250 },
          { fieldName: ListsPropertyNames.attainment, type: ListFilterTypes.numeric, width: 250 },
          { fieldName: ListsPropertyNames.sessionId, type: ListFilterTypes.id, width: 250 },
          { fieldName: ListsPropertyNames.tags, type: ListFilterTypes.array, width: 250 },
        ],
        predefinedFilterRule: [
          [
            {
              fieldName: ListsPropertyNames.sessionId,
              dataType: ListFilterTypes.id,
              operator: SupportedOperators.isOneOf,
              dynamicFilters: [{ dynamicValueType: DynamicFilters.currentSessions }],
            },
            {
              fieldName: ListsPropertyNames.ownerIds,
              dataType: ListFilterTypes.id,
              operator: SupportedOperators.contains,
              dynamicFilters: [{ dynamicValueType: DynamicFilters.currentUser, value: [currentUserId] }],
            },
          ],
        ],
        predefinedSortingRule: [{ fieldName: ListsPropertyNames.attainment, reversed: true }],
      },
      [ListTypes["Key Result"]]: {
        predefinedColumns: [
          { fieldName: ListsPropertyNames.name, type: ListFilterTypes.alphabetic, width: 250 },
          { fieldName: ListsPropertyNames.ownerIds, type: ListFilterTypes.id, width: 250 },
          { fieldName: ListsPropertyNames.attainment, type: ListFilterTypes.numeric, width: 250 },
          { fieldName: ListsPropertyNames.goalId, type: ListFilterTypes.id, width: 250 },
          { fieldName: ListsPropertyNames.lastCheckInDate, type: ListFilterTypes.date, width: 250 },
          { fieldName: ListsPropertyNames.confidenceValue, type: ListFilterTypes.numeric, width: 250 },
        ],
        predefinedFilterRule: [
          [
            {
              fieldName: ListsPropertyNames.sessionId,
              dataType: ListFilterTypes.id,
              operator: SupportedOperators.isOneOf,
              dynamicFilters: [{ dynamicValueType: DynamicFilters.currentSessions }],
            },
            {
              fieldName: ListsPropertyNames.ownerIds,
              dataType: ListFilterTypes.id,
              operator: SupportedOperators.contains,
              dynamicFilters: [{ dynamicValueType: DynamicFilters.currentUser, value: [currentUserId] }],
            },
          ],
        ],
        predefinedSortingRule: [{ fieldName: ListsPropertyNames.attainment, reversed: true }],
      },
      [ListTypes.Task]: {
        predefinedColumns: [
          { fieldName: ListsPropertyNames.name, type: ListFilterTypes.alphabetic, width: 250 },
          { fieldName: ListsPropertyNames.ownerId, type: ListFilterTypes.id, width: 250 },
          { fieldName: ListsPropertyNames.dueDate, type: ListFilterTypes.date, width: 250 },
          { fieldName: ListsPropertyNames.status, type: ListFilterTypes.alphabetic, width: 250 },
          { fieldName: ListsPropertyNames.parentId, type: ListFilterTypes.id, width: 250 },
        ],
        predefinedFilterRule: [
          [
            {
              fieldName: ListsPropertyNames.ownerId,
              dataType: ListFilterTypes.id,
              operator: SupportedOperators.isOneOf,
              dynamicFilters: [{ dynamicValueType: DynamicFilters.currentUser, value: [currentUserId] }],
            },
          ],
        ],
        predefinedSortingRule: [{ fieldName: ListsPropertyNames.dueDate, reversed: true }],
      },
      [ListTypes.Employee]: {
        predefinedColumns: [
          { fieldName: ListsPropertyNames.picture, type: ListFilterTypes.alphabetic, width: 100 },
          { fieldName: ListsPropertyNames.firstName, type: ListFilterTypes.alphabetic, width: 150 },
          { fieldName: ListsPropertyNames.lastName, type: ListFilterTypes.alphabetic, width: 150 },
          { fieldName: ListsPropertyNames.teamIds, type: ListFilterTypes.id, width: 250 },
          { fieldName: ListsPropertyNames.goalCount, type: ListFilterTypes.numeric, width: 140 },
          { fieldName: ListsPropertyNames.progress, type: ListFilterTypes.numeric, width: 140 },
          { fieldName: ListsPropertyNames.manualMetricCount, type: ListFilterTypes.numeric, width: 140 },
          { fieldName: ListsPropertyNames.dynamicMetricCount, type: ListFilterTypes.numeric, width: 140 },
        ],
        predefinedFilterRule: [
          [
            {
              fieldName: ListsPropertyNames.teamIds,
              dataType: ListFilterTypes.id,
              operator: SupportedOperators.contains,
              dynamicFilters: [{ dynamicValueType: DynamicFilters.currentUserTeams }],
            },
          ],
        ],
        predefinedSortingRule: [{ fieldName: ListsPropertyNames.firstName, reversed: false }],
      },
      [ListTypes.Team]: {
        predefinedColumns: [
          { fieldName: ListsPropertyNames.avatar, type: ListFilterTypes.alphabetic, width: 100 },
          { fieldName: ListsPropertyNames.name, type: ListFilterTypes.alphabetic, width: 150 },
          { fieldName: ListsPropertyNames.manager, type: ListFilterTypes.id, width: 250 },
          { fieldName: ListsPropertyNames.members, type: ListFilterTypes.id, width: 250 },
          { fieldName: ListsPropertyNames.teamGoalCount, type: ListFilterTypes.numeric, width: 140 },
          { fieldName: ListsPropertyNames.progress, type: ListFilterTypes.numeric, width: 140 },
        ],
        predefinedFilterRule: [
          [
            {
              fieldName: ListsPropertyNames.members,
              dataType: ListFilterTypes.id,
              operator: SupportedOperators.contains,
              dynamicFilters: [{ dynamicValueType: DynamicFilters.currentUserTeamsMember }],
            },
          ],
        ],
        predefinedSortingRule: [{ fieldName: ListsPropertyNames.name, reversed: false }],
      },
    };
  }

  public getPredefinedListRules(type: ListTypes): { predefinedFilterRule: OrListFiltersGroup[]; predefinedSortingRule: IListSort[]; predefinedColumns: IListColumn[] } {
    const currentUserId = getCurrentUserId();
    return this.predefinedListRulesByType(currentUserId)[type];
  }

  public getOperatorsDictionary(): Record<string, unknown> {
    const alphabetic = {
      longer: localize("is_longer_than"),
      shorter: localize("is_shorter_than"),
      contains: localize("contains"),
      doesNotContain: localize("does_not_contain"),
      notEqual: localize("is_not_equal_to"),
      equals: localize("equals_to"),
      isOneOf: localize("is_one_of"),
      isNotOneOf: localize("is_not_one_of"),
    };

    const containsNotContains = {
      contains: localize("contains"),
      doesNotContain: localize("does_not_contain"),
    };

    return {
      alphabetic,
      id: {
        notEqual: localize("is_not"),
        equals: localize("is"),
        isOneOf: localize("is_one_of"),
        isNotOneOf: localize("is_not_one_of"),
      },
      date: {
        notEqual: localize("is_not"),
        equals: localize("is"),
        greater: localize("is_after"),
        less: localize("is_before"),
        isBefore: localize("is_before"),
        isAfter: localize("is_after"),
        isBetween: localize("is_between"),
        isNotBetween: localize("is_not_between"),
        missingOrEmpty: localize("never").toLowerCase(),
      },
      numeric: {
        notEqual: localize("is_not_equal_to"),
        equals: localize("equals_to"),
        greater: localize("is_greater_than"),
        less: localize("is_lower_than"),
        isOneOf: localize("is_one_of"),
        isNotOneOf: localize("is_not_one_of"),
      },
      boolean: {
        equals: localize("is"),
        notEqual: localize("is_not"),
        isOneOf: localize("is_one_of"),
        isNotOneOf: localize("is_not_one_of"),
      },
      dropdown: alphabetic,
      multiselect: containsNotContains,
      selectassignee: containsNotContains,
    };
  }

  public getAllSupportedListTypes(): IPromise<ListTypes[]> {
    const url = this.env.getApiEndpoint("lists/supported-types");

    return this.$http.get<{ supportedTypes: ListTypes[] }>(url).then((response) => response.data.supportedTypes);
  }

  public getDictionaryByListType(type: string): Record<string, string> {
    return this[`${type}PropertiesDictionary`]();
  }

  public metricPropertiesDictionary(): Record<string, string> {
    return {
      goalId: localize("objective_cap"),
      dateCreated: localize("date_created_cap"),
      "confidence.reason": localize("last_update_note_cap"),
      target: localize("target_cap"),
      // will be removed
      manualType: localize("manual_type_cap"),
      initialValue: localize("initial_cap"),
      actual: localize("actual_cap"),
      name: capitalize(localize("key_result_name")),
      targetOperator: localize("key_result_type_cap"),
      attainment: localize("progress_cap"),
      "confidence.value": localize("confidence_level_cap"),
      "confidence.date": localize("confidence_level_date_cap"),
      createdById: localize("the_user_who_created_cap"),
      ownerId: localize("owner_cap"),
      ownerIds: localize("owners_cap"),
      lastCheckInDate: localize("last_updated_at_cap"),
      description: localize("description_cap"),
      dueDate: localize("hard_deadline_cap"),
      sessionId: localize("session_cap"),
      sessionTitle: localize("session_cap"),
      goalOwnerId: localize("objective_owner"),
      goalOwnerIds: localize("objective_owners"),
      goalDescription: localize("objective_description"),
      goalAttainment: localize("objective_progress"),
      goalClosedStatus: localize("okr_state"),
    };
  }

  public taskPropertiesDictionary(): Record<string, string> {
    return {
      parentId: capitalize(localize("linked_to")),
      status: localize("status_cap"),
      dueDate: localize("due_date_cap"),
      name: capitalize(localize("task")),
      externalId: localize("external_id_cap"),
      externalSystem: localize("external_system_cap"),
      description: localize("description_cap"),
      ownerId: localize("owner_cap"),
      priority: localize("priority_cap"),
      createdById: localize("created_by_cap"),
      dateCreated: localize("date_created_cap"),
      sessionId: localize("session_cap"),
      sessionTitle: localize("session_cap"),
      closedStatus: localize("okr_state"),
    };
  }

  public employeePropertiesDictionary(): Record<string, string> {
    return {
      firstName: localize("first_name_cap"),
      lastName: localize("last_name_cap"),
      badges: localize("badges_cap"),
      teamIds: localize("teams_cap"),
      goalCount: localize("active_objectives_cap"),
      progress: localize("progress_cap"),
      manualMetricCount: localize("manual_key_results_cap"),
      dynamicMetricCount: localize("dynamic_key_results_cap"),
      checkinCount: localize("kr_updates_cap"),
      userRoles: localize("roles"),
    };
  }

  public teamPropertiesDictionary(): Record<string, string> {
    return {
      name: localize("name_cap"),
      manager: localize("manager_cap"),
      members: localize("team_members_cap"),
      teamGoalCount: localize("active_team_objectives_cap"),
      progress: localize("team_progress_cap"),
      badges: localize("badges_cap"),
      membersGoalCount: localize("active_team_members_objectives_cap"), // Active team members' objectives
      dynamicMetricCount: localize("dynamic_key_results_cap"),
      manualMetricCount: localize("manual_key_results_cap"),
    };
  }

  public filterSchemaByFeature(data: IListPropertyTypes): IPromise<void> {
    const fieldNamesToSkip: string[] = [];
    const promises = this.getFieldsToSkip(data.type, fieldNamesToSkip);
    return this.$q.all(promises).then(() => {
      if (data.fields && data.fields.length) {
        data.fields = data.fields.filter((c) => !fieldNamesToSkip.includes(c.fieldName));
      }
    });
  }

  public filterListColumnSortAndRuleBoundsByFeature(data: IList): IPromise<void> {
    const fieldNamesToSkip: string[] = [];
    const promises = this.getFieldsToSkip(data.type, fieldNamesToSkip);
    return this.$q.all(promises).then(() => {
      if (data.columns && data.columns.length) {
        data.columns = data.columns.filter((c) => !fieldNamesToSkip.includes(c.fieldName));
      }

      if (data.sort && data.sort.length) {
        data.sort = data.sort.filter((c) => !fieldNamesToSkip.includes(c.fieldName));
      }

      if (data.filter && data.filter.ruleBounds) {
        data.filter.ruleBounds = data.filter.ruleBounds.filter((c) => !fieldNamesToSkip.includes(c.fieldName));
      }
    });
  }

  private getFieldsToSkip(type: string, fieldNamesToSkip: string[]): IPromise<void>[] {
    const featureFlagColumnChecks: Record<string, IFilterByFeatureFlag> = {
      [FeatureFlag.ModuleBadges]: {
        fieldNamesToFilter: ["badges"],
        typesToFilterColumns: [ListTypes.Team, ListTypes.Employee],
        typesToFilterSchema: ["team", "user"],
      },
      [FeatureFlag.FeatureUserRoles]: {
        fieldNamesToFilter: ["userRoles"],
        typesToFilterColumns: [ListTypes.Employee],
        typesToFilterSchema: ["user"],
      },
    };

    const promises: IPromise<void>[] = [];
    Object.keys(featureFlagColumnChecks).forEach((f) => {
      const featureSetting = featureFlagColumnChecks[f];
      if (featureSetting.typesToFilterColumns.includes(type) || featureSetting.typesToFilterSchema.includes(type)) {
        promises.push(
          this.featureToggleService.isFeatureAvailable(f).then((enabled) => {
            if (!enabled) {
              fieldNamesToSkip.push(...featureSetting.fieldNamesToFilter);
            }
          })
        );
      }
    });

    return promises;
  }
}
